import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Import icons
import { FiCheck } from 'react-icons/fi';
import { FiCalendar } from 'react-icons/fi';
import { FiMail } from 'react-icons/fi';
import { FiUsers } from 'react-icons/fi';
import { FiArrowRight } from 'react-icons/fi';
import { FiX } from 'react-icons/fi';
import { FiClock } from 'react-icons/fi';
import { FiAlertCircle } from 'react-icons/fi';
import { FiBriefcase } from 'react-icons/fi';

// --- Animation Variants ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { type: 'spring', stiffness: 100 }
  },
};

const importantItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    scale: [1, 1.02, 1],
    transition: {
      type: 'spring',
      stiffness: 100,
      scale: {
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut',
      },
    },
  },
};

function App() {
  const [showPopup, setShowPopup] = useState(false);
  const [nextWorkshopDate, setNextWorkshopDate] = useState('');

  const getNextSaturdayWorkshop = () => {
    const now = new Date();
    const currentDay = now.getDay(); // 0 (Sun) to 6 (Sat)
    const currentHour = now.getHours();
    
    let nextSaturday = new Date();
    
    // Saturday is 6
    if (currentDay === 6 && currentHour < 20) {
      // Today is Saturday and it's before 8 PM
      nextSaturday.setHours(20, 0, 0, 0);
    } 
    else {
      // Find next Saturday
      const daysUntilNextSaturday = (6 - currentDay + 7) % 7 || 7;
      nextSaturday.setDate(now.getDate() + daysUntilNextSaturday);
      nextSaturday.setHours(20, 0, 0, 0);
    }
    
    return nextSaturday;
  };

  const formatWorkshopDate = (date) => {
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Asia/Kolkata'
    };
    return date.toLocaleDateString('en-US', options);
  };

  const getTimeUntilWorkshop = (workshopDate) => {
    const now = new Date();
    const timeDiff = workshopDate.getTime() - now.getTime();
    
    if (timeDiff <= 0) {
      return "Workshop is starting now!";
    }
    
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} and ${hours} hour${hours !== 1 ? 's' : ''} to go!`;
    } else {
      return `${hours} hour${hours !== 1 ? 's' : ''} to go!`;
    }
  };

  const handleWhatsAppClick = () => {
    window.open('https://chat.whatsapp.com/GWMhCNCoUGhFuBkqFUUMhL', '_blank');
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  useEffect(() => {
    const updateWorkshopDate = () => {
      const nextWorkshop = getNextSaturdayWorkshop();
      setNextWorkshopDate(formatWorkshopDate(nextWorkshop));
    };

    updateWorkshopDate();
    const interval = setInterval(updateWorkshopDate, 60000);

    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 12000); // 12 seconds delay for better UX
    
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, []);

  const nextWorkshop = getNextSaturdayWorkshop();
  const timeUntilWorkshop = getTimeUntilWorkshop(nextWorkshop);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 font-['Inter']">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-center">
            <h1 className="text-2xl font-bold text-gray-800">10XAI Founder Workshop</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Main Congratulations Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="bg-green-50 border-2 border-green-200 rounded-3xl p-8 md:p-12 mb-8 shadow-sm">
            <div className="flex justify-center mb-6">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 150, damping: 15, delay: 0.2 }}
                className="bg-green-500 rounded-full p-5 shadow-lg shadow-green-200"
              >
                <FiCheck className="text-5xl text-white" />
              </motion.div>
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-green-700 mb-4 tracking-tight">
              Congratulations! 🎉
            </h1>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-8">
              Payment Confirmed & Seat Reserved!
            </h2>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border-2 border-green-400 shadow-md inline-block mb-8"
            >
              <p className="text-xl md:text-2xl font-bold text-green-800 flex items-center justify-center">
                <FiBriefcase className="mr-3" />
                A warm welcome to all Doctors joining us! 💼
              </p>
            </motion.div>

            <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
              You've successfully secured your spot for just <span className="font-bold text-gray-800">₹49</span>. Get ready to learn how to automate your professional workflows, reclaim 20+ hours a week, and lead the AI revolution in your industry! 🚀
            </p>
          </div>
        </motion.div>

        {/* Status Confirmation */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-10 border-l-8 border-green-500 flex items-center justify-between"
        >
          <div>
            <p className="text-sm uppercase tracking-widest text-gray-500 font-bold mb-1">Status</p>
            <h3 className="text-xl font-bold text-gray-800">₹49 Registration Confirmed ✅</h3>
          </div>
          <div className="hidden md:block bg-green-50 p-3 rounded-full">
            <FiCheck className="text-green-500 text-3xl" />
          </div>
        </motion.div>

        {/* Next Steps Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-white rounded-3xl shadow-xl p-8 md:p-10 mb-10 border border-gray-100"
        >
          <div className="flex items-center justify-center space-x-3 mb-8">
            <div className="h-px bg-gray-200 w-12 hidden md:block"></div>
            <h3 className="text-3xl font-black text-gray-800">
              Your Roadmap 📋
            </h3>
            <div className="h-px bg-gray-200 w-12 hidden md:block"></div>
          </div>
          
          <div className="space-y-8">
            {/* Step 1 */}
            <motion.div variants={itemVariants} className="flex items-start space-x-6">
              <div className="bg-blue-600 text-white rounded-2xl w-12 h-12 flex-shrink-0 flex items-center justify-center font-black text-xl shadow-lg shadow-blue-100">1</div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-900 flex items-center">
                  <FiMail className="mr-2 text-blue-600" />
                  Check Your Inbox 📧
                </h4>
                <p className="text-gray-600 mt-2 text-lg">
                  A confirmation email with the workshop details has been sent to you.
                </p>
                <div className="mt-4 flex items-start space-x-3 text-sm bg-blue-50 p-4 rounded-xl border border-blue-100">
                  <FiAlertCircle className="text-blue-600 mt-0.5 flex-shrink-0 text-lg" />
                  <p className="text-blue-900 font-medium">
                    Note: If you don't see it in your <span className="font-bold">Inbox</span>, please check your <span className="font-bold">Promotions</span> or <span className="font-bold">Spam</span> folders. Mark it as "Not Spam" to ensure you receive the session link!
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Step 2 */}
            <motion.div variants={itemVariants} className="flex items-start space-x-6">
              <div className="bg-purple-600 text-white rounded-2xl w-12 h-12 flex-shrink-0 flex items-center justify-center font-black text-xl shadow-lg shadow-purple-100">2</div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-900 flex items-center">
                  <FiCalendar className="mr-2 text-purple-600" />
                  Block Your Time 📅
                </h4>
                <div className="mt-4 p-6 bg-purple-50 rounded-2xl border-2 border-purple-100 shadow-sm">
                  <p className="text-purple-900 font-extrabold text-xl mb-1">
                    📅 Date: {nextWorkshopDate}
                  </p>
                  <p className="text-purple-800 font-bold text-lg">
                    ⏰ Time: 8:00 PM IST (Saturday)
                  </p>
                  <div className="mt-3 inline-flex items-center px-4 py-1.5 bg-white rounded-full text-purple-600 text-sm font-bold border border-purple-200">
                    <FiClock className="mr-2" />
                    <span>{timeUntilWorkshop}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div variants={importantItemVariants} className="flex items-start space-x-6">
              <div className="bg-green-600 text-white rounded-2xl w-12 h-12 flex-shrink-0 flex items-center justify-center font-black text-xl shadow-lg shadow-green-100">3</div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-900 flex items-center">
                  <FiUsers className="mr-2 text-green-600" />
                  Join the Private Community 🔥
                </h4>
                <p className="text-gray-700 mt-2 text-lg font-medium">
                  This is the most critical step! Join the WhatsApp group for direct access to the workshop links and exclusive AI tools tailored for professionals.
                </p>
                
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleWhatsAppClick}
                  className="mt-6 bg-green-600 text-white px-8 py-4 rounded-2xl font-black text-lg flex items-center justify-center space-x-3 shadow-xl shadow-green-100 hover:bg-green-700 transition-all w-full md:w-auto"
                >
                  <FiUsers className="text-2xl" />
                  <span>Join WhatsApp Community Now</span>
                  <FiArrowRight />
                </motion.button>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Main CTA Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-3xl shadow-2xl p-10 text-white mb-16 relative overflow-hidden"
        >
          <div className="relative z-10 text-center">
            <h3 className="text-3xl md:text-4xl font-black mb-6">
              Don't Miss the Live Link!
            </h3>
            <p className="text-xl text-green-50 mb-10 max-w-2xl mx-auto font-medium opacity-90">
              The workshop link is shared exclusively inside our community 15 minutes before we start. Join 500+ professionals already waiting!
            </p>
            
            <motion.button
              animate={{ scale: [1, 1.03, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleWhatsAppClick}
              className="bg-white text-green-700 font-black text-xl px-10 py-5 rounded-2xl shadow-2xl flex items-center justify-center mx-auto space-x-3"
            >
              <span>🚀 Secure My Access on WhatsApp</span>
              <FiArrowRight />
            </motion.button>
          </div>
          
          <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-black/10 rounded-full blur-3xl"></div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1 }}
          className="border-t pt-10 mt-16 text-center text-gray-500"
        >
          <p className="mb-4 text-lg">
            Need help? Reach out at:
            <a href="mailto:support@yinguru.com" className="text-blue-600 font-bold hover:underline ml-2">
              support@yinguru.com
            </a>
          </p>
          <p className="text-sm font-medium tracking-wide text-gray-400">
            © 2026 10XAI Founder Workshop. Empowering professionals through automation.
          </p>
        </motion.div>
      </div>

      {/* Persistence Popup */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              className="bg-white rounded-[2rem] shadow-2xl max-w-md w-full p-10 relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-400 to-emerald-600"></div>
              
              <button
                onClick={closePopup}
                className="absolute top-6 right-6 text-gray-300 hover:text-gray-600 transition-colors"
              >
                <FiX size={28} />
              </button>
              
              <div className="text-center">
                <div className="bg-green-50 rounded-3xl w-24 h-24 flex items-center justify-center mx-auto mb-8 shadow-inner border border-green-100">
                  <FiUsers className="text-5xl text-green-600" />
                </div>
                
                <h3 className="text-3xl font-black text-gray-900 mb-4">
                  One Last Step! 📢
                </h3>
                
                <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                  The workshop link and exclusive AI checklists are shared <strong>only inside the WhatsApp community</strong>. Make sure you don't miss out!
                </p>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    handleWhatsAppClick();
                    closePopup();
                  }}
                  className="bg-green-600 text-white font-black text-xl px-8 py-5 rounded-2xl w-full shadow-xl shadow-green-100 hover:bg-green-700 transition-all flex items-center justify-center space-x-3"
                >
                  <span>Yes, Join Group! 🚀</span>
                </motion.button>
                
                <button
                  onClick={closePopup}
                  className="text-gray-400 hover:text-gray-600 mt-6 text-sm font-bold transition-colors underline"
                >
                  I'll join later
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;